import { Box, Link, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatTime } from './layout'

const FULL_TIER_PX = 56
const COMPACT_TIER_PX = 34
const VISIBLE_TEACHERS = 2

const tint = (theme, tone, weight) =>
  alpha(theme.palette[tone].main, theme.palette.mode === 'dark' ? weight.dark : weight.light)

const surface = (theme, tone, weight) => {
  const colour = tint(theme, tone, weight)
  return {
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `linear-gradient(${colour}, ${colour})`,
  }
}

const CourseName = ({ course, canView, ...props }) =>
  canView ? (
    <Link
      component={NavLink}
      to={`/settings/scheduling/course-sessions/${course.id}`}
      underline="hover"
      color="inherit"
      sx={{ '@media print': { color: 'inherit', textDecoration: 'none' } }}
      {...props}
    >
      {course.name}
    </Link>
  ) : (
    <>{course.name}</>
  )

export default function SessionCard({ entry, pxPerHour, canView, compactText }) {
  const { t } = useTranslation()
  const { session, conflict, lane, laneCount, topHours, heightHours, start, end } = entry

  const height = heightHours * pxPerHour
  const tone = conflict ? 'error' : 'primary'
  const courses = session.list ?? []
  const [primary] = courses
  const teachers = primary?.teachers ? primary.teachers.split(' | ') : []
  const room = primary?.room?.name

  const shown = teachers.slice(0, VISIBLE_TEACHERS)
  const hidden = teachers.length - shown.length

  const tooltip = (
    <Box>
      <Typography variant="caption" display="block" fontWeight={700}>
        {conflict ? t('Course conflict') : primary?.name}
      </Typography>
      {conflict && courses.map(course => (
        <Typography key={course.id} variant="caption" display="block">
          {course.name}
        </Typography>
      ))}
      <Typography variant="caption" display="block">
        {formatTime(start)} – {formatTime(end)}
      </Typography>
      {room && <Typography variant="caption" display="block">{room}</Typography>}
      {teachers.map(teacher => (
        <Typography key={teacher} variant="caption" display="block">{teacher}</Typography>
      ))}
      {!teachers.length && !conflict && (
        <Typography variant="caption" display="block">{t('No Teacher')}</Typography>
      )}
    </Box>
  )

  const nameSx = {
    fontSize: compactText ? 10 : 12,
    lineHeight: 1.2,
    fontWeight: 600,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: height >= FULL_TIER_PX ? 2 : 1,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  }
  const detailSx = { fontSize: compactText ? 9 : 11, lineHeight: 1.2, color: 'text.secondary' }

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <Box
        sx={theme => ({
          position: 'absolute',
          top: topHours * pxPerHour,
          height,
          left: `calc(${(lane / laneCount) * 100}% + 2px)`,
          width: `calc(${(1 / laneCount) * 100}% - 4px)`,
          overflow: 'hidden',
          px: 0.75,
          py: 0.85,
          borderRadius: 1,
          borderLeft: '3px solid',
          borderColor: theme.palette[tone].main,
          color: 'text.primary',
          ...surface(theme, tone, { light: 0.08, dark: 0.18 }),
          '&:hover': surface(theme, tone, { light: 0.14, dark: 0.26 }),
          '@media print': { boxShadow: 'none' },
        })}
      >
        {conflict ? (
          <>
            <Typography noWrap sx={{ ...nameSx, WebkitLineClamp: 1, color: 'error.main' }}>
              {t('Course conflict')}
            </Typography>
            {height >= COMPACT_TIER_PX &&
              courses.map(course => (
                <Typography key={course.id} noWrap sx={detailSx}>
                  <CourseName course={course} canView={canView} />
                </Typography>
              ))}
          </>
        ) : (
          <>
            <Typography sx={nameSx}>
              <CourseName course={primary ?? {}} canView={canView && Boolean(primary?.id)} />
            </Typography>

            {height >= FULL_TIER_PX && (
              <>
                {room && <Typography noWrap sx={detailSx}>{room}</Typography>}
                {shown.map(teacher => (
                  <Typography key={teacher} noWrap sx={detailSx}>{teacher}</Typography>
                ))}
                {hidden > 0 && <Typography noWrap sx={detailSx}>{`+${hidden}`}</Typography>}
              </>
            )}

            {height >= COMPACT_TIER_PX && height < FULL_TIER_PX && (room || teachers.length > 0) && (
              <Typography noWrap sx={detailSx}>
                {[room, teachers[0]].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Tooltip>
  )
}
